import {
  type SelectOption,
  Button,
  ButtonVariant,
  FormMessage,
  FormMessageVariant,
  Input,
  InputGroup,
  Placement,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isEmpty, isNil, snakeCase } from 'lodash';
import { MapTool } from 'map-3d/components/map-3d-container/map-3d-tools';
import { FeatureInfoTool, LineEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
  setSelectedPointCloudLayer,
} from 'map-3d/shared/map-3d-slices';
import { ToolEvent } from 'map-3d/shared/map-3d-tools/enums';
import type { IFeatureInfoTool } from 'map-3d/shared/map-3d-tools/interfaces';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FeatureGeometryType,
  FeatureType,
  handleResponseErrorMessage,
  LayerType,
  useFeatureList,
} from 'shared/api';
import { Placeholders } from 'shared/constants';
import { preventNonNumber } from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import {
  type WorkspaceLayer,
  type WorkspaceLayerListObj,
  CrossSectionErrorMessage,
  MarkArea,
  MarkAreaDrawingToolType,
  MarkAreaToggle,
} from '../../shared';
import { setVectorLayerVisibility, zoomToLayer } from '../map-3d-workspace';
import { crossSectionFormInitialState, getDrawnLineStyle } from './constants';
import { TooltipInfoCopy } from './enums';
import {
  autoSelectPointCloudLayer,
  deleteBlueOverlayLines,
  deleteLinesByMarkedArea,
  flyToSelectedLine,
  handleFeatureFocus,
  loadLinesFromWorkspace,
  prepareCrossSectionLineData,
  updateLineStylesForSampleDistance,
} from './helpers';
import type {
  CrossSectionFormMessage,
  CrossSectionFormProps,
  CrossSectionFormState,
} from './types';

export const CrossSectionForm: React.FC<CrossSectionFormProps> = ({
  onGenerateCrossSection,
  onOpenGraphViewer,
  isGenerating,
  isGraphVisible,
}) => {
  // States.
  const [currentMarkedArea, setCurrentMarkedArea] = useState(MarkArea.Draw);
  const [isDrawLineToolEnabled, setIsDrawLineToolEnabled] = useState(false);
  const [featureInfoTool, setFeatureInfoTool] = useState<IFeatureInfoTool>();
  const [formMessage, setFormMessage] =
    useState<CrossSectionFormMessage | null>(null);

  // Hooks.
  const { values, setValues, names, errors, setErrors, onBlur, onFocus } =
    useInputFields<CrossSectionFormState>(crossSectionFormInitialState);

  // Dispatch.
  const dispatch = useAppDispatch();

  // Selectors.
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);
  const { activeWorkspaceSite } = useAppSelector(selectMap3dDataset);
  const { workspaceLayers } = useAppSelector(selectMap3dWorkspace);
  const activeLineLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(state, FeatureGeometryType.Line),
  );

  // Drawing tools.
  const lineTool = aoiManager?.lineTool;

  // Memoized values.
  const visiblePointCloudLayers = useMemo(
    () =>
      Object.values(workspaceLayers).filter(
        (layer) => layer.show && layer.type === LayerType.PointCloud,
      ),
    [workspaceLayers],
  );
  const pointCloudLayerOptions: SelectOption<WorkspaceLayer>[] = useMemo(
    () =>
      visiblePointCloudLayers.map((layer) => ({
        label: layer.name,
        value: layer,
      })),
    [visiblePointCloudLayers],
  );
  const lineLayersSelectOptions: SelectOption<WorkspaceLayer>[] = useMemo(
    () =>
      activeLineLayers.map((layer) => ({
        label: layer.name,
        value: layer,
      })),
    [activeLineLayers],
  );
  const visibleWorkspaceLayers: WorkspaceLayerListObj = useMemo(() => {
    return Object.fromEntries(
      Object.entries(workspaceLayers).filter(([, layer]) => layer.show),
    );
  }, [workspaceLayers]);

  // Callbacks.
  const onFeatureFocusListener = useCallback(
    ([featureId]: [string]) => {
      handleFeatureFocus(
        featureId,
        lineTool,
        currentMarkedArea,
        values.sampleDistance,
        setValues,
      );
    },
    [currentMarkedArea, values.sampleDistance, setValues],
  );

  // API.
  const {
    data: featuresResponse,
    isFetching: isLoadingFeaturesResponse,
    isSuccess: isSuccessFeaturesResponse,
    isError: isErrorFeaturesResponse,
    error: featuresResponseError,
    refetch: refetchFeatures,
  } = useFeatureList(
    values.workspaceLineLayer?.value.id ?? null,
    FeatureType.LineString,
    false,
  );

  // useEffects.
  useEffect(() => {
    if (isNil(cesiumProxy)) {
      return;
    }

    const highestZIndexLayer = autoSelectPointCloudLayer(
      visiblePointCloudLayers,
      setValues,
    );

    if (!isNil(highestZIndexLayer)) {
      dispatch(setSelectedPointCloudLayer(highestZIndexLayer.id));
    }

    return () => {
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      featureInfoTool?.deactivate();
    };
  }, []);

  useEffect(() => {
    if (isSuccessFeaturesResponse && !isNil(featuresResponse)) {
      loadLinesFromWorkspace(lineTool, featuresResponse.data.features);
    }

    handleResponseErrorMessage(isErrorFeaturesResponse, featuresResponseError);
  }, [
    isSuccessFeaturesResponse,
    featuresResponse,
    isErrorFeaturesResponse,
    featuresResponseError,
  ]);

  useEffect(() => {
    if (isNil(values.workspaceLineLayer)) {
      return;
    }

    refetchFeatures();
    deleteLinesByMarkedArea(lineTool, MarkArea.LayerFromWorkspace);
    setValues((prevValues) => ({ ...prevValues, selectedLine: null }));
  }, [values.workspaceLineLayer, setValues]);

  useEffect(() => {
    if (isNil(cesiumProxy?.viewer)) {
      return;
    }

    const infoTool = new FeatureInfoTool(cesiumProxy.cesiumViewer);
    setFeatureInfoTool(infoTool);
    lineTool?.toggleClampTo3DTileset(true);

    infoTool.addEventListener(ToolEvent.Focus, onFeatureFocusListener);

    return () => {
      infoTool.removeEventListener(ToolEvent.Focus, onFeatureFocusListener);
      lineTool?.toggleClampTo3DTileset(false);
    };
  }, [cesiumProxy?.viewer, onFeatureFocusListener]);

  const lineCreatedHandler = () => {
    const drawnLines = lineTool?.getLinesByProperty(
      'crossSectionLines',
      MarkArea.Draw,
    );
    if (!isNil(drawnLines) && !isEmpty(drawnLines)) {
      setValues((prevValues) => ({
        ...prevValues,
        selectedLine: drawnLines[0],
      }));
      setFormMessage(null);
    }
  };

  const startDrawingHandler = () => {
    const boundaryWidth = Number(values.sampleDistance);

    lineTool?.deleteAllLines();
    lineTool?.activate(
      { crossSectionLines: MarkArea.Draw },
      getDrawnLineStyle(boundaryWidth),
      true,
    );
    lineTool?.addEventListener(LineEventType.LineCreated, lineCreatedHandler);

    dispatch(setCurrentActiveMapTool(MapTool.None));
    setIsDrawLineToolEnabled(true);
    setFormMessage(null);
    setValues((prevValues) => ({
      ...prevValues,
      workspaceLineLayer: null,
      selectedLine: null,
    }));
  };

  const endDrawingHandler = () => {
    lineTool?.removeEventListener(
      LineEventType.LineCreated,
      lineCreatedHandler,
    );
    lineTool?.deactivate();
    setIsDrawLineToolEnabled(false);

    const drawnLines = lineTool?.getLinesByProperty(
      'crossSectionLines',
      MarkArea.Draw,
    );
    if (isNil(drawnLines) || isEmpty(drawnLines)) {
      setValues((prevValues) => ({ ...prevValues, selectedLine: null }));
      lineTool?.clearIncompleteDrawing();
    }
  };

  const resetDrawingHandler = () => {
    const boundaryWidth = Number(values.sampleDistance);
    lineTool?.deleteAllLines();
    lineTool?.clearIncompleteDrawing();
    lineTool?.deactivate();
    lineTool?.activate(
      { crossSectionLines: MarkArea.Draw },
      getDrawnLineStyle(boundaryWidth),
      true,
    );
    setValues((prevValues) => ({ ...prevValues, selectedLine: null }));
  };

  const onToggleChange = (toggledValue: string) => {
    const toggleValueAsEnum = snakeCase(toggledValue) as MarkArea;
    setIsDrawLineToolEnabled(false);
    setFormMessage(null);
    featureInfoTool?.deactivate();

    if (toggleValueAsEnum === MarkArea.Draw) {
      deleteLinesByMarkedArea(lineTool, MarkArea.LayerFromWorkspace);
      deleteBlueOverlayLines(lineTool);
      setVectorLayerVisibility(visibleWorkspaceLayers, true);
      setErrors({ ...errors, workspaceLineLayer: '' });
    } else {
      setVectorLayerVisibility(workspaceLayers, false);
      deleteLinesByMarkedArea(lineTool, MarkArea.Draw);
      lineTool?.deactivate();

      if (isEmpty(lineLayersSelectOptions)) {
        setErrors({
          ...errors,
          workspaceLineLayer: CrossSectionErrorMessage.NoActiveLineLayer,
        });
      }
    }

    setValues((prevValues) => ({
      ...prevValues,
      workspaceLineLayer: null,
      selectedLine: null,
    }));
    setCurrentMarkedArea(toggleValueAsEnum);
  };

  const handleSampleDistanceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    if (isEmpty(value) || parseFloat(value) >= 0) {
      setValues({ ...values, sampleDistance: value });

      const boundaryWidth = Number(value);

      updateLineStylesForSampleDistance(lineTool, boundaryWidth);

      if (!isNil(cesiumProxy)) {
        cesiumProxy.viewer.scene.requestRender();
      }
    }
  };

  const handleWorkspaceLayerChange = (
    selectedOption: SelectOption<WorkspaceLayer>,
  ) => {
    deleteBlueOverlayLines(lineTool);
    setValues({
      ...values,
      workspaceLineLayer: selectedOption,
      selectedLine: null,
    });

    if (selectedOption) {
      featureInfoTool?.activate();
      setFormMessage({
        message: CrossSectionErrorMessage.SelectLineFeature,
        variant: FormMessageVariant.Info,
      });
    }
  };

  const handlePointCloudLayerChange = (
    selectedOption: SelectOption<WorkspaceLayer>,
  ) => {
    setValues({ ...values, pointCloudLayer: selectedOption });

    if (selectedOption) {
      zoomToLayer(selectedOption.value, cesiumProxy, activeWorkspaceSite);
      dispatch(setSelectedPointCloudLayer(selectedOption.value.id));
    }
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isNil(values.selectedLine)) {
      return;
    }

    flyToSelectedLine(values.selectedLine, cesiumProxy);

    const lineData = prepareCrossSectionLineData(
      values.selectedLine,
      values.sampleDistance,
    );

    if (lineData) {
      onGenerateCrossSection(lineData);
    }
  };

  // Derived state.
  const isGenerateDisabled =
    isNil(values.pointCloudLayer) ||
    isNil(values.selectedLine) ||
    isEmpty(values.sampleDistance) ||
    isGenerating;
  const selectedLayer = values.pointCloudLayer?.value;

  return (
    <form className="cross-section-form" onSubmit={onSubmit}>
      <div className="cross-section-form__content map-3d-sidebar-content">
        {/* Point Cloud Details Section */}
        <div className="cross-section-form__section">
          <div className="cross-section-form__section-title">
            Point Cloud Details
          </div>
          <InputGroup>
            <Input.Label isRequired>Select Layer</Input.Label>
            <Input.Select
              options={pointCloudLayerOptions}
              value={values.pointCloudLayer}
              name={names.pointCloudLayer}
              placeholder="Select Point Cloud"
              isDisabled={isGenerating}
              onChange={handlePointCloudLayerChange}
              {...{ onBlur, onFocus }}
            />
          </InputGroup>
        </div>

        {selectedLayer && (
          <div className="cross-section-form__layer-info">
            <div className="cross-section-form__layer-info-item">
              <span className="cross-section-form__layer-info-label">
                Site:
              </span>
              <span className="cross-section-form__layer-info-value">
                {selectedLayer.site.name ?? 'N/A'}
              </span>
            </div>
            <div className="cross-section-form__layer-info-item">
              <span className="cross-section-form__layer-info-label">
                Iteration:
              </span>
              <span className="cross-section-form__layer-info-value">
                {selectedLayer.iteration.name ?? 'N/A'}
              </span>
            </div>
          </div>
        )}

        {/* Mark Line of Interest Section */}
        <MarkAreaToggle
          headerComponent={
            <Input.Label
              isRequired
              className="cross-section-form__section-title cross-section-form__mark-area-title"
            >
              Mark Area of Interest
            </Input.Label>
          }
          currentMarkedArea={currentMarkedArea}
          onToggleChange={onToggleChange}
          drawingHandlers={{
            endDrawingHandler,
            startDrawingHandler,
            resetDrawingHandler,
          }}
          isDrawingToolEnabled={isDrawLineToolEnabled}
          workspaceLayerError={errors.workspaceLineLayer}
          selectLayerSelectProps={{
            options: lineLayersSelectOptions,
            value: values.workspaceLineLayer,
            isDisabled: isEmpty(lineLayersSelectOptions) || isGenerating,
            name: names.workspaceLineLayer,
            isLoading: isLoadingFeaturesResponse,
            placeholder: Placeholders.SelectLayerFromWorkspace,
            onChange: handleWorkspaceLayerChange,
            onBlur,
            onFocus,
          }}
          drawingType={MarkAreaDrawingToolType.Line}
          isDisabled={isGenerating}
        />

        {formMessage && (
          <FormMessage
            message={formMessage.message}
            variant={formMessage.variant}
          />
        )}

        {/* Sample Distance Section */}
        <InputGroup className="cross-section-form__sample-distance">
          <Input.Label
            info={TooltipInfoCopy.SampleDistance}
            infoPlacement={Placement.Top}
            className="cross-section-form__sample-distance__label"
            isRequired
          >
            <div className="cross-section-form__sample-distance__title">
              Sample Distance
              <span className="cross-section-form__sample-distance__title-unit">
                (m)
              </span>
            </div>
          </Input.Label>
          <Input.Number
            value={values.sampleDistance}
            name={names.sampleDistance}
            min={0.1}
            step={0.1}
            onKeyDown={preventNonNumber}
            disabled={isGenerating}
            placeholder="Sample Distance"
            onChange={handleSampleDistanceChange}
            {...{ onBlur, onFocus }}
          />
        </InputGroup>
      </div>

      {/* Footer Buttons */}
      <div className="cross-section-form__footer">
        <Button
          variant={ButtonVariant.Link}
          onClick={onOpenGraphViewer}
          disabled={isGraphVisible}
          type="button"
        >
          Open Graph Viewer
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          type="submit"
          disabled={isGenerateDisabled}
          isLoading={isGenerating}
        >
          {isGenerating ? 'Generating' : 'Generate Cross Section'}
        </Button>
      </div>
    </form>
  );
};
